/*
 * The one call this app makes: GET /api/v1/marketplace on the Next app.
 *
 * The response types are written out here rather than imported, because the
 * server's copy lives in apps/web/src/lib/marketplace.ts beside drizzle and
 * importing it would pull the database client into a browser bundle. Keep the
 * two in step; the server's is the source of truth.
 */

export type Day = 'today' | 'tomorrow'

export interface MarketplaceSlot {
  at: string
  label: string
}

export interface MarketplaceResult {
  slug: string
  name: string
  tagline: string
  industry: string
  group: string | null
  address: string | null
  logoUrl: string | null
  accent: string | null
  accentInk: string | null
  url: string
  acceptingBookings: boolean
  service: { name: string; feeInr: number | null; kind: 'appointment' | 'lead' } | null
  fromInr: number | null
  serviceCount: number
  availability: { dayLabel: string; slots: MarketplaceSlot[] } | null
}

export interface MarketplaceResponse {
  query: { text: string; understood: string[]; area: string | null; group: string | null; day: Day | null }
  total: number
  results: MarketplaceResult[]
  groups: { id: string; name: string; blurb: string; count: number }[]
  areas: { name: string; count: number }[]
}

export interface SearchParams {
  q?: string
  group?: string | null
  area?: string | null
  limit?: number
}

/**
 * Empty in production, where /api is same-origin. Set VITE_API_BASE to point a
 * build at another host (https://api.blooq.eastlaunch.com) — the endpoint
 * answers CORS for exactly that.
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')

export async function searchMarketplace(params: SearchParams, signal?: AbortSignal): Promise<MarketplaceResponse> {
  const query = new URLSearchParams()
  if (params.q) query.set('q', params.q)
  if (params.group) query.set('group', params.group)
  if (params.area) query.set('area', params.area)
  if (params.limit) query.set('limit', String(params.limit))

  const response = await fetch(`${API_BASE}/api/v1/marketplace?${query}`, { signal })
  if (!response.ok) throw new Error(`marketplace ${response.status}`)
  return (await response.json()) as MarketplaceResponse
}
